/**
 * شاشة «التقييم والترشيح» — `/evaluation` (design/Evaluation.dc.html).
 *
 * الشاشة دي بتفك **وقفتين** من وقفات رحلة الطالب الجديد:
 *   • خطوة ٤ «حجز حصة التقييم» — مفيش كود كان بيكتب في `bookings` خالص.
 *   • خطوة ٦ «الترشيح» — كان بيتنشر من بوت تليجرام بس، والبوت مقفول.
 *
 * 🔴 **الموعد بتوقيت الأسرة.** الموظفة بتكتب الساعة اللي اتفقت عليها مع
 *    الأسرة، والشاشة بتحوّلها لتوقيت عالمي قبل ما تبعتها، وبتعرض الموعد
 *    بالتوقيتين قبل الحفظ. الأسرة في كندا والمقيّمة في مصر.
 *
 * 🔴 **الأسرة اللي مالهاش منطقة زمنية ما بيتحجزلهاش.** الزرار بيبقى مقفول
 *    ومكتوب جنبه السبب — مش زرار بيضغط وياخد رفض في وشه.
 *
 * ⛔ مفيش شبكة إتاحات مقيّمين: كوليكشن `availability` فاضي، وشبكة مخترعة
 *    كانت هتخلّي الشاشة تكدب. الموعد بيتكتب بإيد الموظفة والسيستم بيمنع
 *    التعارض.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Ltr, fmtDate, fmtNum, fmtTime } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  SUBJECTS,
  cancelBooking,
  createBooking,
  createNomination,
  fetchBookings,
  fetchEvaluationRequests,
  fetchEvaluators,
  fetchNominations,
  fetchEvaluationSessions,
  fetchRubric,
  fetchTeacherCandidates,
  respondToNomination,
  submitEvaluation,
  PROFILE_FIELDS,
  type ProfileField,
  type BookingRow,
  type EvaluationRequest,
  type EvaluationSession,
  type EvalSessionStudent,
  type Evaluator,
  type NominationRow,
  type RubricForm,
  type TeacherOption,
} from "@/api/evaluation";
import {
  claimTask,
  completeTask,
  newIdempotencyKey,
  setChecklistItem,
} from "@/api/tasks";

const DURATIONS = [20, 30, 45, 60];
/** توقيت الأكاديمية — الشاشة المعتمدة بتقول «بتوقيت القاهرة» حرفيًا */
const ACADEMY_TZ = "Africa/Cairo";

type Load =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/* ══════════════════ تحويل الوقت ══════════════════
   الموظفة بتكتب «الثلاثاء ٦م» **بتوقيت الأسرة**. لازم نحوّلها للحظة
   عالمية واحدة قبل ما نبعتها، وإلا كل واحد هيقرا رقم تاني.
   ⚠️ التحويل بيتعمل مرتين عشان حدود التوقيت الصيفي: أول تخمين ممكن يقع
      على إزاحة قديمة، فبنقيس الإزاحة تاني عند اللحظة المحسوبة. */

function tzOffsetMs(at: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(at)) parts[p.type] = p.value;
  const asUtc = Date.UTC(
    Number(parts["year"]),
    Number(parts["month"]) - 1,
    Number(parts["day"]),
    Number(parts["hour"]) % 24,
    Number(parts["minute"]),
    Number(parts["second"]),
  );
  return asUtc - at.getTime();
}

/** «2026-09-02» + «18:00» بتوقيت `tz` ⇒ لحظة عالمية (ISO) */
export function wallClockToUtcIso(date: string, time: string, tz: string): string | null {
  if (!date || !time) return null;
  const naive = Date.parse(`${date}T${time}:00Z`);
  if (Number.isNaN(naive)) return null;
  const first = naive - tzOffsetMs(new Date(naive), tz);
  const second = naive - tzOffsetMs(new Date(first), tz);
  return new Date(second).toISOString();
}

/* ══════════════════ الشاشة ══════════════════ */

export function Evaluation() {
  const { param } = useRouter();
  const leadFilter = param("lead");

  const [state, setState] = useState<Load>({ kind: "loading" });
  const [requests, setRequests] = useState<EvaluationRequest[]>([]);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [nominations, setNominations] = useState<NominationRow[]>([]);
  const [sessions, setSessions] = useState<EvaluationSession[]>([]);
  const [rubric, setRubric] = useState<RubricForm | null>(null);
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const [reqs, bks, noms, evs, sess] = await Promise.all([
        fetchEvaluationRequests(),
        fetchBookings({}),
        fetchNominations("open"),
        fetchEvaluators(),
        fetchEvaluationSessions(),
      ]);
      setRequests(reqs);
      setBookings(bks.rows);
      setBookingsTotal(bks.total);
      setNominations(noms);
      setEvaluators(evs);
      setSessions(sess);
      setState({ kind: "ready" });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت
      console.error("[evaluation] تحميل الشاشة فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    fetchTeacherCandidates(null).then(
      (rows) => alive && setTeachers(rows),
      (err: unknown) => {
        console.error("[evaluation] قايمة المعلمين مش متاحة — الترشيح هيتقفل", err);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  // 🔑 بنود الروبرك **من السيرفر** ونسختها بتتخزّن مع كل تقييم — الواجهة
  //    ما بتكتبش البنود عندها. لو المسار وقع، فورم النتيجة بيتقفل ومكتوب ليه
  //    بدل ما يفتح على روبرك مخترع.
  useEffect(() => {
    let alive = true;
    fetchRubric().then(
      (r) => alive && setRubric(r),
      (err: unknown) => {
        console.error("[evaluation] بنود الروبرك مش متاحة — فورم النتيجة هيتقفل", err);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  const shownRequests = useMemo(
    () => (leadFilter ? requests.filter((r) => r.family.id === leadFilter) : requests),
    [requests, leadFilter],
  );
  const blockedCount = shownRequests.filter((r) => !r.canBook).length;
  const openNominations = nominations.filter((n) => n.status === "open");
  // عدد الطلاب اللي لسه ما اتسجّلتش نتيجتهم — مش عدد الحصص
  const pendingResults = sessions.reduce((n, s) => n + s.remaining, 0);

  const crumb = (
    <>
      <span className="num">{fmtNum(shownRequests.length)}</span> طلب منتظر الحجز ·{" "}
      <span className="num">{fmtNum(pendingResults)}</span> نتيجة مستنية ·{" "}
      <span className="num">{fmtNum(openNominations.length)}</span> ترشيح مفتوح
    </>
  );

  return (
    <Shell title="التقييم والترشيح" crumb={crumb}>
      {toast ? (
        <div className="chip okc" role="status">
          <span className="bullet" />
          {toast}
        </div>
      ) : null}

      {leadFilter && state.kind === "ready" && shownRequests.length === 0 ? (
        <div className="card" style={{ padding: 16 }}>
          <div className="t-body">الطلب ده مش في قايمة «منتظر الحجز» دلوقتي.</div>
          <div className="t-meta">
            يا إما الحجز اتعمل خلاص، يا إما الرحلة لسه ما وصلتش لخطوة الحجز.
          </div>
        </div>
      ) : null}

      {state.kind === "loading" ? <LoadingState rows={5} /> : null}
      {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
      {state.kind === "error" ? (
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
      ) : null}

      {state.kind === "ready" ? (
        <>
          <RequestsCard
            rows={shownRequests}
            evaluators={evaluators}
            blockedCount={blockedCount}
            onDone={(msg) => {
              showToast(msg);
              void load();
            }}
          />
          <BookingsCard
            rows={bookings}
            total={bookingsTotal}
            teachers={teachers}
            onDone={(msg) => {
              showToast(msg);
              void load();
            }}
          />
          <SessionsCard
            rows={sessions}
            rubric={rubric}
            onDone={(msg) => {
              showToast(msg);
              void load();
            }}
          />
          <NominationsCard
            rows={openNominations}
            onDone={(msg) => {
              showToast(msg);
              void load();
            }}
          />
        </>
      ) : null}
    </Shell>
  );
}

/* ══════════════════ ① طلبات منتظرة الحجز ══════════════════ */

function RequestsCard({
  rows,
  evaluators,
  blockedCount,
  onDone,
}: {
  rows: EvaluationRequest[];
  evaluators: Evaluator[];
  blockedCount: number;
  onDone: (msg: string) => void;
}) {
  const [openFor, setOpenFor] = useState<EvaluationRequest | null>(null);
  // ⛔ زرار أساسي **واحد** في الشاشة — أول طلب جاهز للحجز بس
  const primaryKey = rows.find((r) => r.canBook);

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
        <span className="sec-title">
          طلبات منتظرة الحجز <span className="num">{fmtNum(rows.length)}</span>
        </span>
        <span className="t-meta">
          {blockedCount > 0 ? (
            <>
              <span className="num">{fmtNum(blockedCount)}</span> منهم مش جاهزين للحجز —
              السبب مكتوب في الصف
            </>
          ) : (
            "المصدر: بنود خطوة «حجز حصة التقييم» المفتوحة في الرحلات"
          )}
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="مفيش طلبات منتظرة الحجز"
          hint="الطلب بيوصل هنا أول ما مكالمة التأكيد تتقفل في الرحلة"
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>الطالب</th>
                <th>الأسرة</th>
                <th>المنطقة الزمنية</th>
                <th>الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.journeyId}:${r.student.id ?? "none"}`} className="row-link">
                  <td className="t-body">
                    {r.student.name_ar || <span className="missing">بلا اسم مسجّل</span>}
                  </td>
                  <td className="t-desc">{r.family.name_ar}</td>
                  <td>
                    {r.family.timezone ? (
                      <span className="chip ghost">
                        <Ltr>{r.family.timezone}</Ltr>
                      </span>
                    ) : (
                      <span className="chip late">
                        <span className="bullet" />
                        مش مسجّلة
                      </span>
                    )}
                  </td>
                  <td>
                    {r.canBook ? (
                      <button
                        type="button"
                        className={`btn sm ${r === primaryKey ? "primary" : "ghost"}`}
                        onClick={() => setOpenFor(r)}
                      >
                        حجز موعد
                      </button>
                    ) : (
                      <>
                        <button type="button" className="btn sm ghost is-off" disabled>
                          حجز موعد
                        </button>
                        <div className="t-meta" style={{ marginTop: 4 }}>
                          {r.blockedReasonAr}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openFor ? (
        <BookingForm
          request={openFor}
          evaluators={evaluators}
          onClose={() => setOpenFor(null)}
          onDone={(msg) => {
            setOpenFor(null);
            onDone(msg);
          }}
        />
      ) : null}
    </div>
  );
}

/* ══════════════════ فورم الحجز ══════════════════ */

function BookingForm({
  request,
  evaluators,
  onClose,
  onDone,
}: {
  request: EvaluationRequest;
  evaluators: Evaluator[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const tz = request.family.timezone ?? "";
  const [evaluatorId, setEvaluatorId] = useState(evaluators[0]?.id ?? "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const iso = useMemo(
    () => (tz ? wallClockToUtcIso(date, time, tz) : null),
    [date, time, tz],
  );

  async function submit() {
    if (!request.student.id) return;
    if (!evaluatorId) {
      setError("اختار المقيّمة الأول");
      return;
    }
    if (!iso) {
      setError("اكتب اليوم والساعة");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await createBooking({
        studentId: request.student.id,
        evaluatorUserId: evaluatorId,
        startAtIso: iso,
        durationMinutes: duration,
        journeyId: request.journeyId,
        stepNo: request.stepNo,
      });
      const extra = out.warningsAr.length ? ` · ${out.warningsAr.join(" · ")}` : "";
      onDone(out.toastAr + extra);
    } catch (err) {
      console.error("[evaluation] الحجز فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
      role="region"
      aria-label="حجز موعد التقييم"
    >
      <div className="sec-h">
        <span className="sec-title">
          حجز موعد — <bdi>{request.student.name_ar}</bdi>
        </span>
        <span className="t-meta">
          الساعة اللي هتكتبها بتوقيت الأسرة (<Ltr>{tz}</Ltr>)
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">المقيّمة</span>
          <select
            className="field"
            value={evaluatorId}
            onChange={(e) => setEvaluatorId(e.target.value)}
          >
            {evaluators.length === 0 ? <option value="">مفيش مقيّمين مسجّلين</option> : null}
            {evaluators.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name_ar}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">اليوم</span>
          <input
            className="field"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">الساعة (بتوقيت الأسرة)</span>
          <input
            className="field"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">المدة</span>
          <select
            className="field"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          >
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {fmtNum(d)} دقيقة
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* 🔑 المعاينة بالتوقيتين — الفرق بين كندا ومصر هو مصدر أخطاء الحجز */}
      {iso ? (
        <div className="t-desc">
          عند الأسرة: <bdi className="num">{`${fmtDate(iso, tz)}، ${fmtTime(iso, tz)}`}</bdi>
          {" · "}
          عند الأكاديمية:{" "}
          <bdi className="num">
            {`${fmtDate(iso, ACADEMY_TZ)}، ${fmtTime(iso, ACADEMY_TZ)}`}
          </bdi>
        </div>
      ) : (
        <div className="t-meta">اكتب اليوم والساعة عشان نعرض الموعد بالتوقيتين.</div>
      )}

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert">
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={`btn primary${busy ? " is-busy" : ""}`}
          disabled={busy || !iso}
          onClick={() => void submit()}
        >
          {busy ? "بيتحجز…" : "تأكيد الموعد"}
        </button>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ ② المواعيد المحجوزة + الترشيح ══════════════════ */

function BookingsCard({
  rows,
  total,
  teachers,
  onDone,
}: {
  rows: BookingRow[];
  /** العدد الكلي من السيستم — الشاشة بتقول لو بتعرض جزء منه بس */
  total: number;
  teachers: TeacherOption[];
  onDone: (msg: string) => void;
}) {
  const [nominateFor, setNominateFor] = useState<BookingRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function cancel(row: BookingRow) {
    const reason = window.prompt("سبب الإلغاء؟");
    if (!reason || reason.trim().length < 2) return;
    setBusy(row.id);
    try {
      onDone(await cancelBooking(row.id, reason.trim()));
    } catch (err) {
      console.error("[evaluation] إلغاء الحجز فشل", err);
      onDone(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
        <span className="sec-title">
          المواعيد <span className="num">{fmtNum(total)}</span>
        </span>
        <span className="t-meta">
          {total > rows.length ? (
            <>
              معروض أول <span className="num">{fmtNum(rows.length)}</span> بس ·{" "}
            </>
          ) : null}
          الموعد بيتعرض بتوقيت الأسرة وتوقيت الأكاديمية
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="مفيش مواعيد متحجزة" hint="احجز من قايمة الطلبات فوق" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>الطالب</th>
                <th>عند الأسرة</th>
                <th>عند الأكاديمية</th>
                <th>المقيّمة</th>
                <th>الحالة</th>
                <th>الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="row-link">
                  <td className="t-body">{b.student.name_ar}</td>
                  <td className="t-desc">
                    {b.whenFamilyAr ? (
                      <bdi className="num">{b.whenFamilyAr}</bdi>
                    ) : (
                      // ⛔ ما بنعيدش الحساب بتوقيت المتصفح — ده توقيت الموظفة
                      <span className="missing">الموعد محتاج مراجعة</span>
                    )}
                  </td>
                  <td className="t-desc">
                    {b.whenAcademyAr ? (
                      <bdi className="num">{b.whenAcademyAr}</bdi>
                    ) : (
                      <span className="missing">—</span>
                    )}
                  </td>
                  <td className="t-desc">{b.evaluator.name_ar}</td>
                  <td>
                    <span
                      className={`chip ${b.status === "cancelled" ? "ghost" : "info"}`}
                    >
                      <span className="bullet" />
                      {b.statusLabelAr}
                    </span>
                  </td>
                  <td>
                    {b.status === "cancelled" ? (
                      <span className="t-meta">مفيش إجراء</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn sm ghost"
                          disabled={busy !== null || !b.student.id}
                          onClick={() => setNominateFor(b)}
                        >
                          رشّح معلم
                        </button>
                        <button
                          type="button"
                          className="btn sm ghost"
                          style={{ marginRight: 6 }}
                          disabled={busy !== null}
                          onClick={() => void cancel(b)}
                        >
                          إلغاء
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nominateFor ? (
        <NominationForm
          booking={nominateFor}
          teachers={teachers}
          onClose={() => setNominateFor(null)}
          onDone={(msg) => {
            setNominateFor(null);
            onDone(msg);
          }}
        />
      ) : null}
    </div>
  );
}

function NominationForm({
  booking,
  teachers,
  onClose,
  onDone,
}: {
  booking: BookingRow;
  teachers: TeacherOption[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [subject, setSubject] = useState(SUBJECTS[0]?.key ?? "quran");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pool = useMemo(
    () => teachers.filter((t) => t.subjects.length === 0 || t.subjects.includes(subject)),
    [teachers, subject],
  );

  async function submit() {
    if (!booking.student.id) return;
    if (picked.length === 0) {
      setError("اختار معلم واحد على الأقل");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await createNomination({
        studentId: booking.student.id,
        subject,
        teacherIds: picked,
      });
      onDone(out.toastAr);
    } catch (err) {
      console.error("[evaluation] الترشيح فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
      role="region"
      aria-label="ترشيح معلم"
    >
      <div className="sec-h">
        <span className="sec-title">
          ترشيح معلم — <bdi>{booking.student.name_ar}</bdi>
        </span>
        <span className="t-meta">
          أول معلم يوافق بيقفل الترشيح عند الباقيين — والرد بيتسجّل من هنا أو من تليجرام
        </span>
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 260 }}>
        <span className="t-meta">المادة</span>
        <select
          className="field"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            setPicked([]);
          }}
        >
          {SUBJECTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.labelAr}
            </option>
          ))}
        </select>
      </label>

      {pool.length === 0 ? (
        <div className="t-meta">
          مفيش معلمين مسجّلين للمادة دي — راجع سجل المعلمين قبل الترشيح.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {pool.slice(0, 20).map((t) => {
            const on = picked.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={`filt${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() =>
                  setPicked((cur) =>
                    cur.includes(t.id) ? cur.filter((x) => x !== t.id) : [...cur, t.id],
                  )
                }
              >
                {t.name_ar}
              </button>
            );
          })}
        </div>
      )}

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert">
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={`btn primary${busy ? " is-busy" : ""}`}
          disabled={busy || picked.length === 0}
          onClick={() => void submit()}
        >
          {busy ? "بيتسجّل…" : `رشّح ${fmtNum(picked.length)} معلم`}
        </button>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ ③ حصص التقييم — تسليم النتيجة ══════════════════
   🔴 **الوقفة اللي الكارت ده بيفكّها:** خطوة «حصة التقييم» بند لكل طالب،
      وماكانش في الواجهة أي مكان بيقفل بند طالب ⇒ المقيّمة تشوف
      «لسه فيه بنود ما خلصوش» **بلا أي طريقة تعملها**.

   الترتيب اللي الكارت بيفرضه هو ترتيب الشغل الحقيقي:
     ① استلم مهمة الخطوة (الاستلام بيثبّت المسؤولية)
     ② سجّل نتيجة كل طالب — البند بيتقفل مع كل نتيجة
     ③ علّم قايمة الخطوة
     ④ اقفل الخطوة — والزرار مقفول ومكتوب سببه لحد ما التلاتة يخلصوا */

function SessionsCard({
  rows,
  rubric,
  onDone,
}: {
  rows: EvaluationSession[];
  /** `null` = مسار الروبرك وقع ⇒ الفورم مقفول ومكتوب ليه */
  rubric: RubricForm | null;
  onDone: (msg: string) => void;
}) {
  const pending = rows.reduce((n, r) => n + r.remaining, 0);
  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
        <span className="sec-title">
          حصص التقييم — سجّل النتيجة <span className="num">{fmtNum(rows.length)}</span>
        </span>
        <span className="t-meta">
          {pending > 0 ? (
            <>
              <span className="num">{fmtNum(pending)}</span> طالب لسه ما اتسجّلتش نتيجته ·
              الخطوة ما بتتقفلش غير لما كلهم يخلصوا
            </>
          ) : (
            "المصدر: مهمة خطوة «حصة التقييم والروبرك» في طابور قسمك"
          )}
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="مفيش حصص مستنية نتيجة"
          hint="الحصة بتوصل هنا أول ما موعد التقييم يتحجز للأسرة"
        />
      ) : (
        rows.map((s) => (
          <SessionBlock key={s.journeyId} session={s} rubric={rubric} onDone={onDone} />
        ))
      )}
    </div>
  );
}

function SessionBlock({
  session,
  rubric,
  onDone,
}: {
  session: EvaluationSession;
  rubric: RubricForm | null;
  onDone: (msg: string) => void;
}) {
  const [openFor, setOpenFor] = useState<EvalSessionStudent | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const task = session.task;

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setError(null);
    try {
      onDone(await fn());
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت — الرسالة بتظهر في مكانها مش في الكونسول بس
      console.error("[evaluation] فعل خطوة التقييم فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--line)", padding: "12px 16px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <strong className="t-body">
          <bdi>{session.family.name_ar}</bdi>
        </strong>
        <span className="chip ghost">
          <span className="num">{fmtNum(session.total - session.remaining)}</span> من{" "}
          <span className="num">{fmtNum(session.total)}</span> اتسجّلوا
        </span>
        {task.claimedByMe ? (
          <span className="chip okc">
            <span className="bullet" />
            مستلمها إنت
          </span>
        ) : (
          <button
            type="button"
            className="btn sm ghost"
            disabled={busy !== null || task.state === "blocked"}
            onClick={() =>
              void run("claim", async () => {
                await claimTask(task.id, task.version, newIdempotencyKey());
                return "استلمت مهمة خطوة التقييم.";
              })
            }
          >
            استلم مهمة الخطوة
          </button>
        )}
      </div>

      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>الطالب</th>
              <th>الموعد عند الأسرة</th>
              <th>النتيجة</th>
              <th>الإجراء</th>
            </tr>
          </thead>
          <tbody>
            {session.students.map((st) => (
              <tr key={st.id ?? st.name_ar}>
                <td className="t-body">
                  <bdi>{st.name_ar || <span className="missing">بلا اسم مسجّل</span>}</bdi>
                </td>
                <td className="t-desc">
                  {st.whenFamilyAr ? (
                    <bdi className="num">{st.whenFamilyAr}</bdi>
                  ) : (
                    // ⛔ ما بنعيدش الحساب بتوقيت المتصفح — ده توقيت الموظفة
                    <span className="missing">مفيش موعد متسجّل</span>
                  )}
                </td>
                <td>
                  {st.done ? (
                    <span className="chip okc">
                      <span className="bullet" />
                      متوسط <span className="num">{st.average ?? "—"}</span>
                    </span>
                  ) : (
                    <span className="chip soon">
                      <span className="bullet" />
                      لسه
                    </span>
                  )}
                </td>
                <td>
                  {st.done ? (
                    <span className="t-meta">اتسجّلت</span>
                  ) : (
                    <button
                      type="button"
                      className="btn sm ghost"
                      disabled={busy !== null || !st.id || !rubric}
                      onClick={() => setOpenFor(st)}
                    >
                      سجّل النتيجة
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!rubric ? (
        <div className="t-meta" style={{ marginTop: 6 }}>
          بنود الروبرك مش متاحة دلوقتي — حدّث الصفحة قبل ما تسجّل نتيجة.
        </div>
      ) : null}

      {/* 🔑 قايمة الخطوة نفسها — كانت مالهاش أي مكان تتعلّم منه */}
      {task.checklist.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
          {task.checklist.map((c) => (
            <label
              key={c.key}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-sm)" }}
            >
              <input
                type="checkbox"
                checked={c.done}
                disabled={busy !== null || !task.claimedByMe}
                onChange={(e) => {
                  const done = e.target.checked;
                  void run(`chk:${c.key}`, async () => {
                    await setChecklistItem(task.id, c.key, done);
                    return done ? `${c.labelAr} — اتعلّمت.` : `${c.labelAr} — اتشالت.`;
                  });
                }}
              />
              <span>{c.labelAr}</span>
              {c.required ? <span className="t-meta">مطلوب</span> : null}
            </label>
          ))}
          {!task.claimedByMe ? (
            <span className="t-meta">استلم المهمة الأول عشان تعلّم البنود.</span>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)", marginTop: 6 }} role="alert">
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <button
          type="button"
          className="btn sm ghost"
          disabled={busy !== null || !session.canClose}
          onClick={() =>
            void run("close", async () => {
              const out = await completeTask(
                task.id,
                "الحصص اتعملت والروبرك اتملى لكل الطلاب",
                task.version,
              );
              return out.toastAr ?? "خطوة التقييم اتقفلت.";
            })
          }
        >
          اقفل خطوة التقييم
        </button>
        {/* 🔑 السبب مكتوب جنب الزرار المقفول — مش زرار بيضغط وياخد رفض */}
        {session.blockedReasonAr ? (
          <span className="t-meta">{session.blockedReasonAr}</span>
        ) : null}
      </div>

      {openFor ? (
        <ResultForm
          session={session}
          student={openFor}
          rubric={rubric}
          onClose={() => setOpenFor(null)}
          onDone={(msg) => {
            setOpenFor(null);
            onDone(msg);
          }}
        />
      ) : null}
    </div>
  );
}

function ResultForm({
  session,
  student,
  rubric,
  onClose,
  onDone,
}: {
  session: EvaluationSession;
  student: EvalSessionStudent;
  rubric: RubricForm | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  // ⛔ **مفيش درجة افتراضية** — البند اللي ما اتحطّلوش درجة بيفضل فاضي،
  //    والسيرفر بيرفض الروبرك الناقص. تعبئة «3» تلقائيًا معناها روبرك
  //    مخترع لطالب المقيّمة ما قيّمتوش في البند ده.
  const [scores, setScores] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [confidence, setConfidence] = useState("");
  const [style, setStyle] = useState("");
  // ── الاستمارة الموسّعة (طلب الأكاديمية) ──
  // ⚠️ كلها اختيارية: الحصة حصلت فعلًا، وإيقاف تسجيل نتيجتها لمجرد إن
  //    المقيّمة ما عرفتش هواية الطالب كان هيخلّي النتيجة تضيع خالص.
  // 🔑 ومطويّة افتراضيًا عشان الفورم ما يخوّفش — الروبرك هو الأساس.
  const [more, setMore] = useState(false);
  const [level, setLevel] = useState("");
  const [profile, setProfile] = useState<Partial<Record<ProfileField, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = rubric?.items ?? [];
  const missing = items.filter((i) => !scores[i.key]);

  async function submit() {
    if (!student.id) return;
    if (missing.length > 0) {
      setError(`فاضل: ${missing.map((m) => m.labelAr).join(" · ")}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await submitEvaluation({
        studentId: student.id,
        journeyId: session.journeyId,
        stepNo: session.stepNo,
        rubric: Object.fromEntries(items.map((i) => [i.key, Number(scores[i.key])])),
        notesAr: notes.trim() || null,
        recommendedSubjects: subjects,
        evaluatorConfidence: confidence || null,
        preferredTeacherStyle: style || null,
        studentLevel: level || null,
        profile,
      });
      const extra = out.warningsAr.length ? ` · ${out.warningsAr.join(" · ")}` : "";
      onDone(out.toastAr + extra);
    } catch (err) {
      console.error("[evaluation] تسجيل النتيجة فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        marginTop: 10,
        paddingTop: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
      role="region"
      aria-label="تسجيل نتيجة حصة التقييم"
    >
      <div className="sec-h">
        <span className="sec-title">
          نتيجة التقييم — <bdi>{student.name_ar}</bdi>
        </span>
        <span className="t-meta">
          المتوسط بيتحسب في السيستم · نسخة الروبرك{" "}
          <span className="num">{fmtNum(rubric?.version ?? 1)}</span>
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {items.map((it) => (
          <label key={it.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="t-meta">{it.labelAr}</span>
            <select
              className="field"
              value={scores[it.key] ?? ""}
              onChange={(e) =>
                setScores((cur) => ({ ...cur, [it.key]: e.target.value }))
              }
            >
              <option value="">—</option>
              {Array.from({ length: it.max - it.min + 1 }, (_, i) => it.min + i).map((n) => (
                <option key={n} value={n}>
                  {fmtNum(n)}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span className="t-meta">ملاحظات المقيّمة</span>
        <textarea
          className="field"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="الطالب متعاون وحفظه كويس، بس التجويد محتاج تأسيس…"
        />
      </label>

      {/* ═══ الاستمارة الموسّعة — مطويّة لحد ما المقيّمة تفتحها ═══ */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          type="button"
          className="btn sm ghost"
          aria-expanded={more}
          onClick={() => setMore((v) => !v)}
          style={{ alignSelf: "flex-start" }}
        >
          {more ? "اخفي بيانات الطالب" : "+ بيانات أكتر عن الطالب (اختياري)"}
        </button>

        {more ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* ⚠️ الجملة دي اتشالت ورجعت في نفس اليوم.
                القديمة كانت بتوعد بتلات حاجات: «بيوصل للمشرفة» و«للمعلم»
                و«بيتحفظ في سجله» — وولا واحدة كانت بتحصل. شلتها، وبعدين
                رسمت الكارت في سجل الطالب فبقت **نُصّها** حقيقي: بيتحفظ في
                السجل، واللي عنده حق يشوفه بيقراه.
                ⛔ و«المعلم» فضلت مشالة: المعلم مالوش حساب في السيستم أصلًا. */}
            <span className="t-meta">
              اللي تكتبيه هنا بيتحفظ في سجل الطالب — واللي هيستلمه بعدك
              بيقراه من هناك.
            </span>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">مستواه من وجهة نظرك</span>
              <select
                className="field"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="">مش محدد</option>
                <option value="beginner">مبتدئ</option>
                <option value="intermediate">متوسط</option>
                <option value="advanced">متقدّم</option>
              </select>
            </label>

            {PROFILE_FIELDS.map((f) => (
              <label key={f.key}
                     style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="t-meta">{f.label}</span>
                <textarea
                  className="field"
                  rows={2}
                  value={profile[f.key] ?? ""}
                  onChange={(e) =>
                    setProfile((prev) => ({ ...prev, [f.key]: e.target.value }))
                  }
                />
              </label>
            ))}
          </div>
        ) : null}
      </div>

      <div>
        <div className="t-meta" style={{ marginBottom: 4 }}>
          التوصية — المواد المقترحة
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {SUBJECTS.filter((s) => s.key !== "unspecified").map((s) => {
            const on = subjects.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                className={`filt${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() =>
                  setSubjects((cur) =>
                    cur.includes(s.key) ? cur.filter((x) => x !== s.key) : [...cur, s.key],
                  )
                }
              >
                {s.labelAr}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">ثقتك في التقييم</span>
          <select
            className="field"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
          >
            <option value="">مش محددة</option>
            {(rubric?.confidence ?? []).map((c) => (
              <option key={c.key} value={c.key}>
                {c.labelAr}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">أسلوب المعلم المناسب</span>
          <select className="field" value={style} onChange={(e) => setStyle(e.target.value)}>
            <option value="">مش محدد</option>
            {(rubric?.styles ?? []).map((s) => (
              <option key={s.key} value={s.key}>
                {s.labelAr}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert">
          {error}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={`btn primary${busy ? " is-busy" : ""}`}
          disabled={busy || missing.length > 0}
          onClick={() => void submit()}
        >
          {busy ? "بيتسجّل…" : "سجّل النتيجة"}
        </button>
        <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

/* ══════════════════ ④ الترشيحات المفتوحة ══════════════════ */

function NominationsCard({
  rows,
  onDone,
}: {
  rows: NominationRow[];
  onDone: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function respond(
    n: NominationRow,
    teacherId: string,
    response: "accepted" | "declined",
  ) {
    let reason: string | null = null;
    if (response === "declined") {
      reason = window.prompt("سبب الاعتذار (اختياري)؟");
    }
    setBusy(`${n.id}:${teacherId}`);
    setError(null);
    try {
      const out = await respondToNomination(n.id, { teacherId, response, reasonAr: reason });
      onDone(out.toastAr);
    } catch (err) {
      console.error("[evaluation] تسجيل رد المعلم فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
        <span className="sec-title">
          الترشيحات المفتوحة <span className="num">{fmtNum(rows.length)}</span>
        </span>
        <span className="t-meta">
          المعلم مالوش حساب — سجّل رده هنا لما يوصلك على أي قناة
        </span>
      </div>

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)", padding: "0 16px 8px" }} role="alert">
          {error}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          title="مفيش ترشيحات مفتوحة"
          hint="رشّح معلمين من جدول المواعيد بعد ما التقييم يخلص"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((n, ni) => (
            <div
              key={n.id}
              style={{ borderTop: "1px solid var(--line)", padding: "12px 16px" }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <strong className="t-body">
                  <bdi>{n.student.name_ar}</bdi>
                </strong>
                <span className="chip ghost">{n.subjectLabelAr}</span>
                {n.missingContextAr.length > 0 ? (
                  <span className="chip soon" title="بيانات ناقصة في كارت المعلم">
                    <span className="bullet" />
                    ناقص: {n.missingContextAr.join(" · ")}
                  </span>
                ) : null}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {n.candidates.map((c, ci) => (
                  <div
                    key={c.teacherId}
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      border: "1px solid var(--line)",
                      borderRadius: "var(--r-control)",
                      padding: "6px 8px",
                    }}
                  >
                    <bdi className="t-body">{c.nameAr}</bdi>
                    <span className="t-meta">{c.responseLabelAr}</span>
                    {c.response === "pending" ? (
                      <>
                        <button
                          type="button"
                          className={`btn sm ${ni === 0 && ci === 0 ? "primary" : "ghost"}`}
                          disabled={busy !== null}
                          onClick={() => void respond(n, c.teacherId, "accepted")}
                        >
                          وافق
                        </button>
                        <button
                          type="button"
                          className="btn sm ghost"
                          disabled={busy !== null}
                          onClick={() => void respond(n, c.teacherId, "declined")}
                        >
                          اعتذر
                        </button>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
